import { httpClient } from './httpClient';

const base = '/api/promociones';

const getAuthHeader = () => {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildSafeName = (value) => {
  const raw = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return raw || "promocion";
};

const getDownloadFilename = (res, fallbackName) => {
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  if (match && match[1]) return match[1];
  return fallbackName || "documento.pdf";
};

export const listarPromociones = () => httpClient.get(base);

export const listarPromocionesDisponibles = async () => {
  const todas = await httpClient.get(base);
  return (Array.isArray(todas) ? todas : []).filter((p) => {
    const activa = p.activo === 1 || p.activo === true;
    const estado = (p.estado || "").toLowerCase();
    const programada = estado === "programado";
    const enCurso = estado === "en_curso" || estado === "cursando";
    const capacidadDefinida = p.capacidad !== null && p.capacidad !== undefined;
    const cupos = p.cupos_disponibles ?? p.cuposDisponibles;
    const cuposNumero = cupos === null || cupos === undefined ? null : Number(cupos);
    const hayCupos = capacidadDefinida ? cuposNumero === null || cuposNumero > 0 : true;

    return activa && (programada || enCurso) && hayCupos;
  });
};

// Solo para uso administrativo (permiso matriculas.excepcion_migracion o root): permite corregir
// matrículas de estudiantes que quedaron sin registrar por un corte de migración de datos
// en promociones que ya cerraron por fecha.
export const listarPromocionesFinalizadas = async () => {
  const todas = await httpClient.get(base);
  return (Array.isArray(todas) ? todas : []).filter((p) => {
    const activa = p.activo === 1 || p.activo === true;
    const estado = (p.estado || "").toLowerCase();
    return activa && estado === "finalizado";
  });
};

export const obtenerPromocion = (id) => httpClient.get(`${base}/${id}`);
export const listarPromocionesPorCurso = (cursoId) => httpClient.get(`${base}/curso/${cursoId}`);
export const obtenerSiguienteNumeroPromocion = (cicloId, cursoId) =>
  httpClient.get(`${base}/next-numero?ciclo_id=${cicloId}&curso_id=${cursoId}`);
export const previsualizarPromocion = ({ cursoId, horarioId, fechaInicio }) => {
  const params = new URLSearchParams();
  if (cursoId) params.append("curso_id", cursoId);
  if (horarioId) params.append("horario_id", horarioId);
  if (fechaInicio) params.append("fecha_inicio", fechaInicio);
  const query = params.toString();
  return httpClient.get(`${base}/previsualizar${query ? `?${query}` : ""}`);
};
export const listarNoLaborablesPromocion = ({ cursoId, desde, hasta }) => {
  const params = new URLSearchParams();
  if (cursoId) params.append("curso_id", cursoId);
  if (desde) params.append("desde", desde);
  if (hasta) params.append("hasta", hasta);
  const query = params.toString();
  return httpClient.get(`${base}/no-laborables${query ? `?${query}` : ""}`);
};
export const listarEstudiantesPorPromocion = (id, options = {}) => {
  const params = new URLSearchParams();
  if (options.horarioId) params.append("horario_id", options.horarioId);
  if (options.excluirAnuladas) params.append("excluir_anuladas", "true");
  const query = params.toString();
  return httpClient.get(`${base}/${id}/estudiantes${query ? `?${query}` : ""}`);
};
export const listarResultadosPromocion = (id, options = {}) => {
  const params = new URLSearchParams();
  if (options.horarioId) params.append("horario_id", options.horarioId);
  if (options.excluirAnuladas) params.append("excluir_anuladas", "true");
  if (options.resultadoCurso) params.append("resultado_curso", options.resultadoCurso);
  if (options.soloAprobados) params.append("solo_aprobados", "true");
  const query = params.toString();
  return httpClient.get(`${base}/${id}/resultados${query ? `?${query}` : ""}`);
};
export const listarDiplomasPromocion = (id) => httpClient.get(`${base}/${id}/diplomas`);
export const listarHorariosInscritosPorPromocion = (id) =>
  httpClient.get(`${base}/${id}/horarios-inscritos`);
export const descargarInscritosPromocion = async (id, promocionNombre) => {
  const url = `${httpClient.baseUrl}${base}/${id}/estudiantes/pdf`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeader(),
      Accept: "application/pdf",
    },
  });

  if (!res.ok) {
    let message = res.statusText || "No se pudo descargar el PDF.";
    const ct = res.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) {
        const data = await res.json();
        if (data?.message) message = data.message;
      } else {
        const text = await res.text();
        if (text) message = text;
      }
    } catch {
      // ignore parse errors
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const fallbackName = `Inscritos-Promo-${buildSafeName(promocionNombre)}.pdf`;
  const filename = getDownloadFilename(res, fallbackName);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 750);

  return true;
};
export const descargarInscritosExcel = async (id, promocionNombre) => {
  const url = `${httpClient.baseUrl}${base}/${id}/estudiantes/excel`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeader(),
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });

  if (!res.ok) {
    let message = res.statusText || "No se pudo descargar el Excel.";
    const ct = res.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) {
        const data = await res.json();
        if (data?.message) message = data.message;
      }
    } catch { /* ignore */ }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const fallbackName = `Inscritos-${buildSafeName(promocionNombre)}.xlsx`;
  const filename = getDownloadFilename(res, fallbackName);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 750);
  return true;
};

export const descargarAsistenciaPromocion = async (id, horarioId, promocionNombre) => {
  const params = new URLSearchParams();
  if (horarioId) params.append("horario_id", horarioId);
  const query = params.toString();
  const url = `${httpClient.baseUrl}${base}/${id}/asistencia/pdf${query ? `?${query}` : ""}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      ...getAuthHeader(),
      Accept: "application/pdf",
    },
  });

  if (!res.ok) {
    let message = res.statusText || "No se pudo descargar el PDF.";
    const ct = res.headers.get("content-type") || "";
    try {
      if (ct.includes("application/json")) {
        const data = await res.json();
        if (data?.message) message = data.message;
      } else {
        const text = await res.text();
        if (text) message = text;
      }
    } catch {
      // ignore parse errors
    }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }

  const blob = await res.blob();
  const fallbackName = `Asistencia-Promo-${buildSafeName(promocionNombre)}.pdf`;
  const filename = getDownloadFilename(res, fallbackName);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 750);

  return true;
};
export const listarHorariosPorPromocion = (id) => httpClient.get(`${base}/${id}/horarios`);
export const obtenerReservasPendientes = (id) => httpClient.get(`${base}/${id}/reservas-pendientes`);
export const asignarReservas = (id, matricula_ids) => httpClient.post(`${base}/${id}/asignar-reservas`, { matricula_ids });
export const crearPromocion = (payload) => httpClient.post(base, payload);
export const actualizarPromocion = (id, payload) => httpClient.put(`${base}/${id}`, payload);
export const eliminarPromocion = (id) => httpClient.del(`${base}/${id}`);
export const guardarResultadosCursoPromocion = (id, resultados = []) =>
  httpClient.post(`${base}/${id}/resultados-curso`, { resultados });
export const guardarAcreditacionesPromocion = (id, resultados = []) =>
  httpClient.post(`${base}/${id}/acreditaciones`, { resultados });
export const obtenerLogResultadoPromocion = (promocionId, estudianteFormacionId) =>
  httpClient.get(`${base}/${promocionId}/resultados/${estudianteFormacionId}/logs`);

// Reporte de asistencia (grilla estudiantes x sesiones, presencial + online)
export const obtenerAsistenciaPromocion = (id) => httpClient.get(`${base}/${id}/asistencia`);
export const obtenerAsistenciaDocentesPromocion = (id) => httpClient.get(`${base}/${id}/asistencia/docentes`);
export const justificarAsistenciaPromocion = (id, sesionId, estudianteId, { estado, observacion }) =>
  httpClient.put(`${base}/${id}/asistencia/sesion/${sesionId}/estudiante/${estudianteId}`, {
    estado,
    observacion,
  });
export const eliminarAsistenciaPromocion = (id, sesionId, estudianteId) =>
  httpClient.del(`${base}/${id}/asistencia/sesion/${sesionId}/estudiante/${estudianteId}`);

// Asistencia por materia/bloque (detalle fino, no afecta el % oficial ni mención de honor)
export const listarBloquesSesionPromocion = (id, sesionId) =>
  httpClient.get(`${base}/${id}/asistencia/sesion/${sesionId}/bloques`);
export const obtenerAsistenciaBloquePromocion = (id, bloqueId) =>
  httpClient.get(`${base}/${id}/asistencia/bloque/${bloqueId}`);
export const justificarAsistenciaBloquePromocion = (id, bloqueId, estudianteId, { estado, observacion }) =>
  httpClient.put(`${base}/${id}/asistencia/bloque/${bloqueId}/estudiante/${estudianteId}`, {
    estado,
    observacion,
  });
export const eliminarAsistenciaBloquePromocion = (id, bloqueId, estudianteId) =>
  httpClient.del(`${base}/${id}/asistencia/bloque/${bloqueId}/estudiante/${estudianteId}`);

const promocionesService = {
  listar: listarPromociones,
  listarDisponibles: listarPromocionesDisponibles,
  listarFinalizadas: listarPromocionesFinalizadas,
  obtener: obtenerPromocion,
  listarPorCurso: listarPromocionesPorCurso,
  listarEstudiantesPorPromocion,
  listarResultadosPromocion,
  listarDiplomasPromocion,
  listarHorariosInscritosPorPromocion,
  descargarInscritosPromocion,
  descargarAsistenciaPromocion,
  listarHorariosPorPromocion,
  obtenerSiguienteNumeroPromocion,
  previsualizarPromocion,
  listarNoLaborablesPromocion,
  crear: crearPromocion,
  actualizar: actualizarPromocion,
  eliminar: eliminarPromocion,
  guardarResultadosCursoPromocion,
  guardarAcreditacionesPromocion,
  obtenerLogResultadoPromocion,
  obtenerAsistenciaPromocion,
  obtenerAsistenciaDocentesPromocion,
  justificarAsistenciaPromocion,
  eliminarAsistenciaPromocion,
  listarBloquesSesionPromocion,
  obtenerAsistenciaBloquePromocion,
  justificarAsistenciaBloquePromocion,
  eliminarAsistenciaBloquePromocion,
};

export default promocionesService;
